// frontend/src/components/cspe/LiasseValidationModal.jsx
import React, { useState, useEffect } from 'react';
import {
  FiX, FiCheck, FiAlertTriangle, FiLoader, FiEdit3,
  FiChevronDown, FiChevronUp, FiFileText
} from 'react-icons/fi';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';

/* ─────────────────────────────────────────────────────────────
   Config des champs – deux sections
───────────────────────────────────────────────────────────── */
const GENERAL_FIELDS = {
  exercice_start_date: { label: "Début d'exercice", type: 'date' },
  exercice_end_date:   { label: "Fin d'exercice",   type: 'date' },
  company_name:        { label: 'Raison sociale',   type: 'text', required: true },
  siret:               { label: 'SIRET',            type: 'text', required: true },
  main_address:        { label: 'Adresse',          type: 'textarea' },
  activities_performed:{ label: 'Activités',        type: 'textarea' },
};

const FISCAL_FIELDS = {
  purchases_goods:               { label: 'FS/234 – Achats marchandises' },
  purchases_raw_materials:       { label: 'FU/238 – Matières premières' },
  other_external_charges:        { label: 'FW/242 – Charges externes' },
  financial_expenses:            { label: 'GR/294 – Charges financières' },
  securities_disposal_charges:   { label: 'GT – Cessions mobilières' },
  sales_products_services:       { label: 'OA/108 – Ventes prod./services' },
  royalties_concessions:         { label: 'OK/118 – Redevances' },
  capital_gains_disposals:       { label: 'OL/119 – Plus-values' },
  cvae_reference_turnover:       { label: "CA référence CVAE" },
  syndical_professional_contributions: { label: 'ES/260 – Cotisations syndicales' },
};

/* ─────────────────────────────────────────────────────────────
   Composant accordéon léger
───────────────────────────────────────────────────────────── */
const Section = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-700 uppercase tracking-wide hover:bg-gray-100 transition-colors"
      >
        {title}
        {open ? <FiChevronUp className="h-3.5 w-3.5" /> : <FiChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && <div className="px-3 py-3 bg-white">{children}</div>}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Champ compact
───────────────────────────────────────────────────────────── */
const Field = ({ label, type = 'text', value, onChange, required, placeholder }) => (
  <div className="flex items-start gap-2 border-b border-gray-50 pb-1.5">
    <span className="text-xs text-gray-500 text-right leading-tight pt-1 shrink-0 w-36">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
    {type === 'textarea' ? (
      <textarea
        rows={2}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 min-w-0 text-xs border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 resize-none"
      />
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        step={type === 'number' ? '0.01' : undefined}
        className="flex-1 min-w-0 text-xs border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
      />
    )}
  </div>
);

/* ─────────────────────────────────────────────────────────────
   Modal principale
───────────────────────────────────────────────────────────── */
const LiasseValidationModal = ({
  isOpen,
  onClose,
  documentId,
  documentName,
  onValidationComplete,
}) => {
  const [loading,          setLoading]          = useState(false);
  const [editableData,     setEditableData]     = useState(null);
  const [adminComments,    setAdminComments]    = useState('');
  const [rejectionReason,  setRejectionReason]  = useState('');
  const [showRejectForm,   setShowRejectForm]   = useState(false);
  const [isValidating,     setIsValidating]     = useState(false);
  const [isRejecting,      setIsRejecting]      = useState(false);
  const [error,            setError]            = useState(null);
  const [isValidated,      setIsValidated]      = useState(false); // ✅ NOUVEAU

  /* ── Chargement ──────────────────────────────────────────── */
  const fetchData = async () => {
    if (!documentId) return;
    setLoading(true); setError(null);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const res = await fetch(`/api/cspe/liasse-validation/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || `Erreur ${res.status}`);
      if (!json.success || !json.data) throw new Error('Données introuvables');

      setEditableData({ ...json.data });
      if (json.data.raw_ocr_data?.adminComments) {
        setAdminComments(json.data.raw_ocr_data.adminComments);
      }
      setIsValidated(json.data.status === 'Validé'); // ✅ NOUVEAU : bandeau vert si déjà validé en DB
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && documentId) fetchData();
  }, [isOpen, documentId]);

  const gardeFouCotisations = editableData?.raw_ocr_data?.extractedData?.cotisations_syndicales_garde_fou || null;

  /* ── Mise à jour d'un champ ──────────────────────────────── */
  const update = (field, value) =>
    setEditableData(prev => ({ ...prev, [field]: value }));

  /* ── Validation ──────────────────────────────────────────── */
  const handleValidate = async () => {
    if (!editableData) return;
    // Valeur signalée par le garde-fou et laissée telle quelle : confirmation explicite.
    if (
      gardeFouCotisations &&
      Number(editableData.syndical_professional_contributions) === Number(gardeFouCotisations.valeur_ocr) &&
      !window.confirm(
        `Les cotisations syndicales (${gardeFouCotisations.valeur_ocr} €) ont été signalées comme copie possible ` +
        `du total « Autres charges » (case 262). Confirmez-vous que la case 260 de la liasse porte bien ce montant ?`
      )
    ) return;
    setIsValidating(true);
    const toastId = toast.loading('Validation en cours…');
    try {
      // Convertir les champs number vides en null
      const cleaned = { ...editableData };
      Object.keys(FISCAL_FIELDS).forEach(k => {
        if (cleaned[k] === '') cleaned[k] = null;
      });

      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const res = await fetch(`/api/cspe/liasse-validation/${documentId}/validate`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ validatedData: cleaned, adminComments: adminComments.trim() }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || `Erreur ${res.status}`);

      toast.success('Liasse validée avec succès !', { id: toastId, duration: 3500 });
      onValidationComplete?.(documentId, 'validated');
      setIsValidated(true); // ✅ NOUVEAU : affiche le bandeau vert, ne ferme plus la modal
    } catch (e) {
      toast.error(e.message, { id: toastId });
    } finally {
      setIsValidating(false);
    }
  };

  /* ── Rejet ───────────────────────────────────────────────── */
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Motif de rejet obligatoire.'); return;
    }
    setIsRejecting(true);
    const toastId = toast.loading('Rejet en cours…');
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const res = await fetch(`/api/cspe/liasse-validation/${documentId}/reject`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejectionReason: rejectionReason.trim() }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || `Erreur ${res.status}`);

      toast.success('Document rejeté.', { id: toastId, icon: '🗑️' });
      onValidationComplete?.(documentId, 'rejected');
      handleClose();
    } catch (e) {
      toast.error(e.message, { id: toastId });
    } finally {
      setIsRejecting(false);
    }
  };

  /* ── Reset & fermeture ───────────────────────────────────── */
  const handleClose = () => {
    setEditableData(null); setAdminComments(''); setRejectionReason('');
    setShowRejectForm(false); setError(null);
    setIsValidated(false); // ✅ NOUVEAU : reset à chaque fermeture
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">

      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* ── Carte modale XS ────────────────────────────────── */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-xl flex flex-col max-h-[90vh]">

        {/* Header compact */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-blue-100">
              <FiFileText className="h-4 w-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 leading-tight">
                Validation manuelle — Liasse Fiscale
              </h3>
              <p className="text-xs text-gray-400 truncate max-w-xs" title={documentName}>
                {documentName}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="ml-2 flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">

          {/* ── État : chargement ── */}
          {loading && (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <FiLoader className="animate-spin h-6 w-6 mr-2" />
              <span className="text-sm">Chargement…</span>
            </div>
          )}

          {/* ── État : erreur ── */}
          {error && !loading && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-md p-3">
              <FiAlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {/* ── Formulaire ── */}
          {editableData && !loading && (
            <>
              {/* Garde-fou post-OCR : affiché hors accordéon pour ne pas être validé sans être vu */}
              {gardeFouCotisations && !isValidated && !editableData.validation_completed_at && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  <FiAlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 leading-snug">
                    <span className="font-semibold">Cotisations syndicales (ES/260) à vérifier : </span>
                    {gardeFouCotisations.regle === 'EGAL_TOTAL_AUTRES_CHARGES'
                      ? `l'OCR a lu ${gardeFouCotisations.valeur_ocr} €, soit exactement le total « Autres charges » (case 262). La case 260 est peut-être vide : dans ce cas, saisir 0.`
                      : `l'OCR a lu ${gardeFouCotisations.valeur_ocr} €, plus que le total « Autres charges » (${gardeFouCotisations.total_autres_charges} €, case 262), ce qui est impossible.`}
                  </p>
                </div>
              )}
              {/* Trace après validation : la valeur signalée a été relue par un admin */}
              {gardeFouCotisations && (isValidated || editableData.validation_completed_at) && (
                <p className="flex items-center gap-1.5 text-[10px] text-gray-500">
                  <FiAlertTriangle className="h-3 w-3 flex-shrink-0" />
                  Signalé à l'extraction : cotisations lues {gardeFouCotisations.valeur_ocr} € pour un total « Autres charges » de {gardeFouCotisations.total_autres_charges} € (case 262).
                </p>
              )}
              {/* Section 1 : Informations générales */}
              <Section title="Informations générales">
              <div className="space-y-1.5">
                <Field label="Début d'exercice"    type="date"     value={editableData.exercice_start_date}   onChange={v => update('exercice_start_date', v)} />
                <Field label="Fin d'exercice"      type="date"     value={editableData.exercice_end_date}     onChange={v => update('exercice_end_date', v)} />
                <Field label="Raison sociale"      type="text"     value={editableData.company_name}          onChange={v => update('company_name', v)}          required placeholder="Nom de la société" />
                <Field label="SIRET"               type="text"     value={editableData.siret}                 onChange={v => update('siret', v)}                 required placeholder="14 chiffres" />
                <Field label="Adresse"             type="textarea" value={editableData.main_address}          onChange={v => update('main_address', v)} />
                <Field label="Activités exercées"  type="textarea" value={editableData.activities_performed}  onChange={v => update('activities_performed', v)} />
              </div>
            </Section>

              {/* Section 2 : Lignes fiscales */}
                <Section title="Lignes fiscales" defaultOpen={false}>
                  <div className="space-y-1.5">
                    {Object.entries(FISCAL_FIELDS).map(([key, { label }]) => (
                      <div key={key} className="flex items-center gap-2 border-b border-gray-50 pb-1">
                        <span
                          className="flex-1 text-xs text-gray-500 text-right truncate"
                          title={label}
                        >
                          {label}
                        </span>
                        <input
                          type="number"
                          value={editableData[key] ?? ''}
                          onChange={e => update(key, e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          className="w-32 shrink-0 text-xs text-right border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    ))}
                  </div>
                </Section>

              {/* Section 3 : Commentaires */}
              <Section title="Commentaires" defaultOpen={false}>
                <textarea
                  rows={3}
                  value={adminComments}
                  onChange={e => setAdminComments(e.target.value)}
                  placeholder="Commentaires optionnels sur la validation ou les corrections…"
                  className="block w-full text-xs border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                />
              </Section>

              {/* Formulaire de rejet (conditionnel) */}
              {showRejectForm && (
                <div className="border border-red-200 rounded-md bg-red-50 px-3 py-3">
                  <label className="block text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
                    <FiAlertTriangle className="h-3.5 w-3.5" /> Motif de rejet <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={3}
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    placeholder="Expliquez pourquoi ces données sont rejetées…"
                    className="block w-full text-xs border-red-300 rounded focus:ring-red-500 focus:border-red-500 resize-none"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* ✅ NOUVEAU : Footer conditionnel */}
        {isValidated ? (

          /* ── Bandeau vert post-validation ── */
          <div className="shrink-0 px-4 py-3 border-t border-green-200 bg-green-50 flex items-center justify-between gap-2 rounded-b-xl">
            <span className="text-green-700 font-medium text-sm flex items-center gap-1.5">
              <FiCheck className="h-4 w-4" />
              ✅ Liasse validée — vous pouvez fermer cette fenêtre
            </span>
            <button
              type="button"
              onClick={handleClose}
              className="px-3 py-1.5 text-xs rounded border border-green-300 text-green-700 hover:bg-green-100 transition-colors"
            >
              Fermer
            </button>
          </div>

        ) : (

          /* ── Footer normal (inchangé) ── */
          <div className="shrink-0 px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-2">

            {/* Gauche : Fermer */}
            <button
              type="button"
              onClick={handleClose}
              className="px-3 py-1.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Fermer
            </button>

            {/* Droite : actions principales */}
            <div className="flex items-center gap-2">

              {/* Rejeter / Confirmer rejet */}
              {!showRejectForm ? (
                <button
                  type="button"
                  onClick={() => setShowRejectForm(true)}
                  disabled={!editableData || loading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40 transition-colors"
                >
                  <FiAlertTriangle className="h-3.5 w-3.5" /> Rejeter
                </button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}
                    className="px-2 py-1.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    disabled={isRejecting || !rejectionReason.trim()}
                    onClick={handleReject}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 transition-colors"
                  >
                    {isRejecting
                      ? <FiLoader className="animate-spin h-3.5 w-3.5" />
                      : <FiAlertTriangle className="h-3.5 w-3.5" />}
                    {isRejecting ? 'Rejet…' : 'Confirmer'}
                  </button>
                </div>
              )}

              {/* Valider */}
              <button
                type="button"
                disabled={isValidating || loading || !editableData}
                onClick={handleValidate}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 transition-colors font-medium"
              >
                {isValidating
                  ? <FiLoader className="animate-spin h-3.5 w-3.5" />
                  : <FiCheck className="h-3.5 w-3.5" />}
                {isValidating ? 'Validation…' : 'Valider'}
              </button>
            </div>
          </div>

        )}
      </div>
    </div>
  );
};

export default LiasseValidationModal;
